import React, { useContext, useEffect, useMemo, useState } from "react";
import { fetchCachedAuthUser, writeCachedAuthUser } from "../utils/authCache";
import { getAuthToken } from "../utils/auth";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

const API_BASE = String(
  import.meta.env.VITE_API_BASE ||
    import.meta.env.VITE_API_BASE_URL ||
    "https://api.avaaio.com"
).replace(/\/+$/, "");

function getProfileAuthHeaders(
  includeJson = true
): Record<string, string> {
  const headers: Record<string, string> = {};

  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }

  const token = getAuthToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

type ProfileShape = {
  name?: string;
  display_name?: string;
  displayName?: string;
  email?: string;
  role?: string;
  plan?: string;
  subscription?: string;
  membership?: string;
  city?: string;
  zip?: string;
  theme?: string;
  theme_color?: string;
  voice?: string;
  voiceName?: string;
  aiMode?: string;
  provider?: string;
  auth_provider?: string;
  loginProvider?: string;
  status?: string;
  memory?: Record<string, any>;
};

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const safe = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${safe}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function getStoredProfile(): ProfileShape | null {
  const keys: string[] = [];

  for (const key of keys) {
    try {
      const raw =
        localStorage.getItem(key) ||
        sessionStorage.getItem(key) ||
        getCookie(key);

      if (!raw) continue;

      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      continue;
    }
  }

  return null;
}

function pretty(value: unknown, fallback = "Not set") {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
}

export default function Profile() {
  const navigate = useNavigate();
  const { themeColors } = useContext(ThemeContext);
  const [apiUser, setApiUser] = useState<ProfileShape | null>(null);
  const [loading, setLoading] = useState(true);

  const storedUser = useMemo(() => getStoredProfile(), []);

  useEffect(() => {
    let cancelled = false;

    fetchCachedAuthUser(`${API_BASE}/api/v1/auth/me`, {
      credentials: "include",
    })
      .then((authUser) => {
        if (cancelled || !authUser) return;
        if (authUser && typeof authUser === "object") {
          setApiUser(authUser);
        }
      })
      .catch((err) => {
        console.warn("[Profile] auth/me read failed:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const merged = {
    ...(storedUser || {}),
    ...(apiUser || {}),
    memory: {
      ...(storedUser?.memory || {}),
      ...(apiUser?.memory || {}),
    },
  };

  const localMemoryLearning =
    typeof window !== "undefined"
      ? localStorage.getItem("ava_memory_learning_enabled")
      : null;

  const memoryLearningEnabled =
    localMemoryLearning === "false"
      ? false
      : localMemoryLearning === "true"
        ? true
        : merged.memory?.learning_enabled === true;

  const displayName = pretty(
  merged.display_name ||
  merged.name ||
  "User"
);

  const email = pretty(merged.email, "Unknown");

  // Access level must come from live backend auth only.
  // Do not allow stale cached profile/memory plan values to upgrade a new/free account.
  const liveRole = String(apiUser?.role || merged.role || "user").toLowerCase();
  const liveSub = String(String((apiUser as any)?.subscription_status || 'inactive')).toLowerCase();
  const liveVip = Boolean((apiUser as any)?.vip_lifetime ?? false);

  const role = pretty(liveRole || "user", "User");

  const isAdminIdentity = ["admin", "owner", "superadmin", "super_admin"].includes(liveRole);
  const isProIdentity = !isAdminIdentity && (liveRole === "vip" || liveSub === "active" || liveVip);

  const plan =
    isAdminIdentity ? "Admin" :
    isProIdentity ? "Pro" :
    "Free";

  // === LIVE IDENTITY STATUS LABEL ===
  const identityStatus =
    isAdminIdentity
      ? "✦ Admin Verified • Command Access Enabled"
      : isProIdentity
        ? "✦ Identity Verified • AVAAIO™ Connected"
        : "✦ Identity Active • Free Access";

  const status = pretty(merged.status || merged.memory?.status);
  const city = pretty(merged.city || merged.memory?.city);
  const rawZip = pretty(merged.zip);
  const zip = rawZip === "12345" ? "Not set" : rawZip;
  const theme = pretty(
    merged.theme ||
      merged.theme_color ||
      merged.memory?.theme_color ||
      localStorage.getItem("ava_theme") ||
      localStorage.getItem("theme"),
    "Default"
  );

  const voice = pretty(
    merged.voice ||
      merged.voiceName ||
      merged.memory?.voice,
    "Default"
  );

  const provider = pretty(
    merged.provider ||
      merged.auth_provider ||
      merged.loginProvider ||
      merged.memory?.provider,
    "Email"
  );

  // EDIT PROFILE MODE START
  const [editingProfile, setEditingProfile] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState(displayName === "Not set" ? "" : displayName);
  const [editZip, setEditZip] = useState(zip === "Not set" ? "" : zip);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveNotice, setProfileSaveNotice] = useState("");

  async function saveEditableProfile(e?: React.MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();
    e?.stopPropagation();
    const cleanName = editDisplayName.trim();
    const cleanZip = editZip.trim().replace(/[^0-9]/g, "").slice(0, 10);
    const finalZip = cleanZip === "12345" ? "" : cleanZip;

    if (!cleanName) {
      alert("Display name cannot be blank.");
      return;
    }

    setProfileSaving(true);
    setProfileSaveNotice("");
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/profile`, {
        method: "PATCH",
        headers: getProfileAuthHeaders(),
        credentials: "include",
          body: JSON.stringify({
            display_name: cleanName,
            zip: finalZip,
          }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.detail || data?.error || `Profile save failed: ${res.status}`);
      }

      // ✅ Update UI immediately from saved values.
      const optimisticUser = {
        ...(apiUser || {}),
        display_name: cleanName,
        name: cleanName,
        zip: cleanZip,
        zip_code: cleanZip,
        memory: {
          ...((apiUser as any)?.memory || {}),
          display_name: cleanName,
          zip: cleanZip,
        },
      };

      try {
        writeCachedAuthUser(optimisticUser);
      } catch (cacheErr) {
        console.warn("[Profile] saved, but cache write skipped:", cacheErr);
      }
      setApiUser(optimisticUser);

      // Keep optimistic saved profile visible. Avoid /auth/me refresh here because mixed cookies can return stale user data.


      setEditingProfile(false);
      console.log("[Profile] Profile updated.");
    } catch (err: any) {
      console.warn("[Profile] profile save warning:", err);
    } finally {
      setProfileSaving(false);
    }
  }
  // EDIT PROFILE MODE END



  async function saveProfileMemoryPatch(memoryPatch: any) {
    const res = await fetch(`${API_BASE}/api/v1/auth/profile`, {
      method: "PATCH",
      headers: getProfileAuthHeaders(),
      credentials: "include",
      body: JSON.stringify({ memory: memoryPatch }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data?.ok === false) {
      throw new Error(
        data?.detail ||
        data?.error ||
        `Profile memory save failed: ${res.status}`
      );
    }

    console.log("[Profile] memory patch saved:", data);
    return data;
  }

  const pageStyle: React.CSSProperties = {
    minHeight: "100vh",
    background: themeColors.bg,
    color: themeColors.text,
    fontFamily: "Orbitron, sans-serif",
    padding: "20px 16px 28px",
    boxSizing: "border-box",
  };

  const shellStyle: React.CSSProperties = {
    maxWidth: "1180px",
    margin: "0 auto",
    padding: "8px",
  };

  const heroStyle: React.CSSProperties = {
    borderRadius: "28px",
    padding: "28px 24px 24px",
    marginBottom: "22px",
    background: themeColors.panel,
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(14px)",
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
  };

  const cardStyle: React.CSSProperties = {
    borderRadius: "28px",
    padding: "20px",
    minHeight: "86px",
    background: themeColors.cardBg,
    border: `1px solid ${themeColors.border}`,
    boxShadow: "0 16px 42px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.025)",
    backdropFilter: "blur(12px)",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "11px",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: themeColors.accent2,
    fontWeight: 800,
    marginBottom: "8px",
  };

  const valueStyle: React.CSSProperties = {
    fontSize: "16px",
    lineHeight: 1.5,
    color: themeColors.text,
    fontWeight: 600,
    wordBreak: "break-word",
  };

  const buttonRowStyle: React.CSSProperties = {
    marginTop: "18px",
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    minHeight: "44px",
    borderRadius: "28px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    color: themeColors.text,
    padding: "0 12px",
    fontWeight: 800,
    letterSpacing: "0.02em",
    outline: "none",
    boxSizing: "border-box",
  };

  const profileEditButtonStyle: React.CSSProperties = {
    minHeight: "42px",
    borderRadius: "28px",
    padding: "0 14px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.text,
    fontWeight: 800,
    fontSize: "13px",
    cursor: "pointer",
    boxShadow: "0 14px 40px rgba(0,0,0,0.32)",
    backdropFilter: "blur(12px)",
    letterSpacing: "0.03em",
  };

  const buttonStyle: React.CSSProperties = {
    minHeight: "48px",
    borderRadius: "28px",
    padding: "0 18px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.text,
    fontWeight: 800,
    fontSize: "14px",
    cursor: "pointer",
    boxShadow: "0 14px 40px rgba(0,0,0,0.32)",
    backdropFilter: "blur(12px)",
    letterSpacing: "0.03em",
  };

  return (
    <div style={pageStyle}>
      <div style={shellStyle}>
        <section style={heroStyle}>
          <div
            style={{
              fontSize: "11px",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: themeColors.accent2,
              fontWeight: 800,
              marginBottom: "8px",
            }}
          >
            AVAAIO Identity
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(34px, 6vw, 68px)",
              lineHeight: 1,
              fontWeight: 900,
              color: themeColors.accent,
              textShadow: themeColors.glow,
              background: `linear-gradient(90deg, ${themeColors.accent2}, ${themeColors.text})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "0.04em",
            }}
          >
            PROFILE
          </h1>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "18px",
              padding: "10px 16px",
              borderRadius: "999px",
              border: `1px solid ${themeColors.border}`,
              background: themeColors.buttonBg,
              color: themeColors.text,
              fontWeight: 800,
              fontSize: "13px",
              letterSpacing: "0.04em",
              boxShadow: themeColors.glow,
              width: "fit-content"
            }}
          >
            {identityStatus}
          </div>

          <p
            style={{
              marginTop: "18px",
              marginBottom: 0,
              maxWidth: "860px",
              color: themeColors.mutedText,
              fontSize: "15px",
              lineHeight: 1.6,
            }}
          >
            Review and control your AVAAIO™ identity, memory, personalization,
            subscription status, and Ava experience from one intelligent command center.
          </p>

          {loading ? (
            <div style={{ marginTop: "14px", color: "rgba(236,250,255,0.72)" }}>
              Loading profile data…
            </div>
          ) : null}
        </section>

        <section style={gridStyle}>
          <div
            style={{
              ...cardStyle,
              background:
                "linear-gradient(180deg, rgba(8,18,32,0.96), rgba(4,10,18,0.92))",
            }}>
            <div style={labelStyle}>Display Name</div>
            {editingProfile ? (
              <input
                style={inputStyle}
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                placeholder="Enter display name"
              />
            ) : (
              <div style={valueStyle}>{displayName}</div>
            )}
          </div>

          <div style={cardStyle}>
            <div style={labelStyle}>Email</div>
            <div
              style={{
                ...valueStyle,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
              title={email}
            >
              {email}
            </div>
          </div>

          <div style={cardStyle}>
            <div style={labelStyle}>Plan</div>
            <div style={valueStyle}>{plan}</div>
          </div>

          <div style={cardStyle}>
            <div style={labelStyle}>Status</div>
            <div style={valueStyle}>{status}</div>
          </div>

          {isAdminIdentity ? (
            <div style={cardStyle}>
              <div style={labelStyle}>Provider</div>
              <div style={valueStyle}>{provider}</div>
            </div>
          ) : null}

          <div style={cardStyle}>
            <div style={labelStyle}>Theme</div>
            <div style={valueStyle}>{theme}</div>
          </div>

          <div style={cardStyle}>
            <div style={labelStyle}>Voice</div>
            <div style={valueStyle}>{voice}</div>
          </div>

          <div style={cardStyle}>
            <div style={labelStyle}>ZIP</div>
            {editingProfile ? (
              <input
                style={inputStyle}
                value={editZip === "12345" ? "" : editZip}
                onChange={(e) => setEditZip(e.target.value)}
                placeholder="Enter ZIP code"
              />
            ) : (
              <div style={valueStyle}>{zip}</div>
            )}
          </div>
        </section>



        {/* ============================= */}
        {/* ============================= */}
        {/* EDIT PROFILE ACTIONS */}
        <div style={buttonRowStyle}>
          {!editingProfile ? (
            <button
              type="button"
              style={profileEditButtonStyle}
              onClick={() => {
                setEditDisplayName(displayName === "Not set" ? "" : displayName);
                setEditZip(zip === "Not set" || zip === "12345" ? "" : zip);
                setEditingProfile(true);
              }}
            >
              Edit Profile
            </button>
          ) : (
            <>
              <button
                type="button"
                style={profileEditButtonStyle}
                disabled={profileSaving}
                onClick={saveEditableProfile}
              >
                {profileSaving ? "Saving..." : "Save Profile"}
              </button>
              <button
                type="button"
                style={profileEditButtonStyle}
                disabled={profileSaving}
                onClick={() => {
                  setEditDisplayName(displayName === "Not set" ? "" : displayName);
                  setEditZip(zip === "Not set" || zip === "12345" ? "" : zip);
                  setEditingProfile(false);
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {/* AVA MEMORY SECTION */}
        <section style={{ marginTop: "28px" }}>
          <div style={cardStyle}>
            <div style={labelStyle}>Memory Learning</div>
            <div style={{ ...valueStyle, display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                type="button"
                aria-label="Toggle memory learning"
                onClick={async () => {
                  const newValue = !memoryLearningEnabled;

                  try {
                    localStorage.setItem("ava_memory_learning_enabled", String(newValue));
                    sessionStorage.setItem("ava_memory_learning_enabled", String(newValue));

                    const raw = localStorage.getItem("ava_user") || sessionStorage.getItem("ava_user");
                    const saved = raw ? JSON.parse(raw) : {};
                    const next = {
                      ...saved,
                      memory: {
                        ...(saved.memory || {}),
                        learning_enabled: newValue,
                      },
                    };
                    localStorage.setItem("ava_user", JSON.stringify(next));
                    sessionStorage.setItem("ava_user", JSON.stringify(next));
                  } catch {}

                  await saveProfileMemoryPatch({
                    learning_enabled: newValue
                  });

                  window.location.reload();
                }}
                style={{
                  width: "52px",
                  height: "28px",
                  borderRadius: "28px",
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: !memoryLearningEnabled ? "#ff3b30" : "#00c853",
                  padding: "3px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: !memoryLearningEnabled ? "flex-start" : "flex-end",
                  transition: "all 0.25s ease"
                }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "28px",
                    background: "#fff",
                    display: "block",
                    boxShadow: "0 0 8px rgba(0,0,0,0.35)"
                  }}
                />
              </button>

              <span>
                {!memoryLearningEnabled ? "Disabled" : "Enabled"}
              </span>
            </div>
          </div>
        </section>
        {/* ============================= */}
        <section style={{ marginTop: "28px" }}>
          <div style={labelStyle}>Ava Memory</div>

          <div style={cardStyle}>
            <div style={{ ...valueStyle, fontSize: "14px", opacity: 0.85 }}>
              Ava uses memory to personalize your experience — including your name,
              preferences, theme, and interaction style.
            </div>

            <div style={{ marginTop: "12px", ...valueStyle }}>
              Memory Status: {merged.memory ? "Active ✦ Personalized" : "Not Active"}
            </div>

            {profileSaveNotice && (
            <div style={{ marginTop: "14px", marginBottom: "10px", color: profileSaveNotice.startsWith("✅") ? "#7CFFB2" : "#FF8A8A", fontWeight: 800 }}>
              {profileSaveNotice}
            </div>
          )}
          <div style={buttonRowStyle}>
              <button
                style={buttonStyle}
                  onClick={async () => {
                    const ok = window.confirm(
                      "Clear Ava memory for this profile?\n\nOK = clear memory\nCancel = keep memory"
                    );

                    if (!ok) return;

                    try {
                      const deleteRes = await fetch(
                        `${API_BASE}/api/v1/auth/profile/memory`,
                        {
                          method: "DELETE",
                          credentials: "include",
                          headers: getProfileAuthHeaders(false),
                        }
                      );

                      const deleteData = await deleteRes
                        .json()
                        .catch(() => ({}));

                      if (!deleteRes.ok || deleteData?.ok === false) {
                        throw new Error(
                          deleteData?.detail ||
                          deleteData?.error ||
                          `Memory delete failed: ${deleteRes.status}`
                        );
                      }
                    } catch (e) {
                      console.warn("[Profile] memory delete failed:", e);
                      alert(
                        "Ava memory could not be cleared. Please try again."
                      );
                      return;
                    }

                    localStorage.removeItem("ava_user");
                    sessionStorage.removeItem("ava_user");

                    localStorage.removeItem("ava_memory");
                    sessionStorage.removeItem("ava_memory");

                    localStorage.removeItem("ava_memory_learning_enabled");
                    sessionStorage.removeItem("ava_memory_learning_enabled");

                    sessionStorage.setItem(
                      "ava_memory_recently_cleared",
                      "true"
                    );

                    alert("Ava memory cleared.");
                    window.location.reload();
                  }}
              >
                Clear Memory
              </button>
            </div>
          </div>
        </section>

        {/* ============================= */}
        {/* AVA ADAPTIVE PERSONALITY */}
        {/* ============================= */}
        <section style={{ marginTop: "20px" }}>
          <div style={labelStyle}>Ava Personality</div>

          <div style={cardStyle}>
            <div style={{ ...valueStyle, fontSize: "14px", opacity: 0.85 }}>
              Ava automatically adapts how she communicates based on your conversation, preferences, and context. Her tone may become more friendly, professional, direct, supportive, or neutral as needed while keeping her core personality consistent.
            </div>
          </div>
        </section></div>
    </div>
  );
}
